import type { ControlFrame } from '../simulation/types';
import { clamp } from '../utils/math';

type TouchAction =
  | 'pitchUp'
  | 'pitchDown'
  | 'rollLeft'
  | 'rollRight'
  | 'yawLeft'
  | 'yawRight'
  | 'fireMissile'
  | 'fireCannon'
  | 'flare'
  | 'targetNext'
  | 'throttleUp'
  | 'throttleDown'
  | 'afterburner'
  | 'pause';

const EMPTY_FRAME: ControlFrame = {
  pitch: 0,
  roll: 0,
  yaw: 0,
  throttleDelta: 0,
  afterburner: false,
  fireMissile: false,
  fireCannon: false,
  deployFlare: false,
  targetNext: false,
  reset: false,
  pause: false,
  start: false,
};

export class InputController {
  private keys = new Set<string>();
  private edges = new Set<string>();
  private touchActions = new Set<TouchAction>();
  private touchEdges = new Set<TouchAction>();
  private pointerDeltaX = 0;
  private pointerDeltaY = 0;
  private zoomDelta = 0;
  private battleInputEnabled = false;
  private mouseSensitivity = 1;
  private invertY = false;
  private touchFlight = { x: 0, y: 0 };
  private touchLook = { x: 0, y: 0 };
  private dragActive = false;

  constructor(private readonly canvas: HTMLCanvasElement) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.clear);
    window.addEventListener('wheel', this.onWheel, { passive: true });
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('contextmenu', (event) => event.preventDefault());
    this.bindTouchControls();
  }

  requestPointerLock(): void {
    // Pointer lock is intentionally disabled. Desktop camera control uses drag-look.
  }

  setPointerLockEnabled(enabled: boolean): void {
    void enabled;
  }

  setBattleInputEnabled(enabled: boolean): void {
    this.battleInputEnabled = enabled;
    if (!enabled) this.clear();
  }

  setMouseSensitivity(value: number): void {
    this.mouseSensitivity = clamp(value, 0.4, 2.4);
  }

  setInvertY(enabled: boolean): void {
    this.invertY = enabled;
  }

  consumeLookDelta(): { yaw: number; pitch: number } {
    const pitchSign = this.invertY ? 1 : -1;
    const look = {
      yaw: this.pointerDeltaX * 0.0034 * this.mouseSensitivity + this.touchLook.x * 0.09,
      pitch: this.pointerDeltaY * 0.0026 * this.mouseSensitivity * pitchSign + this.touchLook.y * 0.07 * pitchSign,
    };
    this.pointerDeltaX = 0;
    this.pointerDeltaY = 0;
    return look;
  }

  consumeZoomDelta(): number {
    const delta = clamp(this.zoomDelta, -2, 2);
    this.zoomDelta = 0;
    return delta;
  }

  consumeFrame(): ControlFrame {
    const frame: ControlFrame = { ...EMPTY_FRAME };

    const pitchKeys = this.axis('KeyS', 'KeyW') + this.axis('ArrowDown', 'ArrowUp');
    const rollKeys = this.axis('KeyA', 'KeyD') + this.axis('ArrowLeft', 'ArrowRight');
    const yawKeys = this.axis('KeyE', 'KeyQ') + (this.touchActions.has('yawRight') ? 1 : 0) - (this.touchActions.has('yawLeft') ? 1 : 0);
    const touchPitch = (this.touchActions.has('pitchUp') ? 1 : 0) - (this.touchActions.has('pitchDown') ? 1 : 0);
    const touchRoll = (this.touchActions.has('rollRight') ? 1 : 0) - (this.touchActions.has('rollLeft') ? 1 : 0);

    frame.pitch = clamp(pitchKeys + touchPitch + this.touchFlight.y, -1, 1);
    frame.roll = clamp(rollKeys + touchRoll + this.touchFlight.x, -1, 1);
    frame.yaw = clamp(yawKeys, -1, 1);
    frame.throttleDelta = this.axis('Equal', 'Minus')
      + this.axis('NumpadAdd', 'NumpadSubtract')
      + (this.touchActions.has('throttleUp') ? 1 : 0)
      - (this.touchActions.has('throttleDown') ? 1 : 0);
    frame.afterburner = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight') || this.touchActions.has('afterburner');
    frame.fireMissile = this.consumeEdge('Space') || this.consumeTouchEdge('fireMissile');
    frame.fireCannon = this.keys.has('KeyC') || this.touchActions.has('fireCannon');
    frame.deployFlare = this.consumeEdge('KeyF') || this.consumeTouchEdge('flare');
    frame.targetNext = this.consumeEdge('Tab') || this.consumeTouchEdge('targetNext');
    frame.reset = this.consumeEdge('Backspace');
    frame.pause = this.consumeEdge('KeyP') || this.consumeTouchEdge('pause');
    frame.start = this.consumeEdge('Enter');

    this.edges.clear();
    this.touchEdges.clear();
    return frame;
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.clear);
    window.removeEventListener('wheel', this.onWheel);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
  }

  private axis(positive: string, negative: string): number {
    return (this.keys.has(positive) ? 1 : 0) - (this.keys.has(negative) ? 1 : 0);
  }

  private consumeEdge(code: string): boolean {
    return this.edges.has(code);
  }

  private consumeTouchEdge(action: TouchAction): boolean {
    return this.touchEdges.has(action);
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (!this.battleInputEnabled || this.isTypingTarget(event.target)) return;
    if (['Space', 'Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Backspace'].includes(event.code)) {
      event.preventDefault();
    }
    if (!this.keys.has(event.code)) this.edges.add(event.code);
    this.keys.add(event.code);
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    if (!this.battleInputEnabled || this.isTypingTarget(event.target)) return;
    this.keys.delete(event.code);
  };

  private readonly onWheel = (event: WheelEvent): void => {
    if (!this.battleInputEnabled) return;
    this.zoomDelta += event.deltaY < 0 ? 1 : -1;
  };

  private readonly onMouseMove = (event: MouseEvent): void => {
    if (!this.battleInputEnabled || !this.dragActive) return;
    this.pointerDeltaX += event.movementX;
    this.pointerDeltaY += event.movementY;
  };

  private readonly onMouseDown = (event: MouseEvent): void => {
    if (!this.battleInputEnabled) return;
    if (event.button !== 0) return;
    if (event.target !== this.canvas) return;
    this.dragActive = true;
    event.preventDefault();
  };

  private readonly onMouseUp = (event: MouseEvent): void => {
    if (event.button !== 0) return;
    this.dragActive = false;
  };

  private readonly clear = (): void => {
    this.keys.clear();
    this.edges.clear();
    this.touchActions.clear();
    this.touchEdges.clear();
    this.touchFlight.x = 0;
    this.touchFlight.y = 0;
    this.touchLook.x = 0;
    this.touchLook.y = 0;
    this.pointerDeltaX = 0;
    this.pointerDeltaY = 0;
    this.zoomDelta = 0;
    this.dragActive = false;
  };

  private isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    const tag = target.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
  }

  private bindTouchControls(): void {
    const buttons = [...document.querySelectorAll<HTMLButtonElement>('[data-touch]')];
    for (const button of buttons) {
      const action = button.dataset.touch as TouchAction | undefined;
      if (!action) continue;
      const start = (event: Event): void => {
        event.preventDefault();
        if (!this.touchActions.has(action)) this.touchEdges.add(action);
        this.touchActions.add(action);
      };
      const end = (event: Event): void => {
        event.preventDefault();
        this.touchActions.delete(action);
      };
      button.addEventListener('pointerdown', start);
      button.addEventListener('pointerup', end);
      button.addEventListener('pointercancel', end);
      button.addEventListener('pointerleave', end);
    }

    this.bindTouchPad('flight-pad', (x, y) => {
      this.touchFlight.x = -x;
      this.touchFlight.y = y;
    }, () => {
      this.touchFlight.x = 0;
      this.touchFlight.y = 0;
    });

    this.bindTouchPad('camera-pad', (x, y) => {
      this.touchLook.x = x;
      this.touchLook.y = y;
    }, () => {
      this.touchLook.x = 0;
      this.touchLook.y = 0;
    });
  }

  private bindTouchPad(
    elementId: string,
    onMove: (x: number, y: number) => void,
    onEnd: () => void,
  ): void {
    const pad = document.getElementById(elementId);
    if (!(pad instanceof HTMLElement)) return;
    const knob = pad.querySelector<HTMLElement>('.touch-pad-knob');
    let pointerId: number | null = null;
    const radius = 44;

    const reset = (): void => {
      pointerId = null;
      pad.classList.remove('engaged');
      if (knob) knob.style.transform = 'translate3d(0, 0, 0)';
      onEnd();
    };

    const update = (clientX: number, clientY: number): void => {
      const rect = pad.getBoundingClientRect();
      const centerX = rect.left + rect.width * 0.5;
      const centerY = rect.top + rect.height * 0.5;
      let dx = clientX - centerX;
      let dy = clientY - centerY;
      const distance = Math.hypot(dx, dy);
      if (distance > radius && distance > 0.001) {
        dx = (dx / distance) * radius;
        dy = (dy / distance) * radius;
      }
      if (knob) knob.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
      onMove(clamp(dx / radius, -1, 1), clamp(dy / radius, -1, 1));
    };

    pad.addEventListener('pointerdown', (event) => {
      if (!this.battleInputEnabled) return;
      event.preventDefault();
      pointerId = event.pointerId;
      pad.classList.add('engaged');
      pad.setPointerCapture?.(event.pointerId);
      update(event.clientX, event.clientY);
    });
    pad.addEventListener('pointermove', (event) => {
      if (pointerId !== event.pointerId) return;
      event.preventDefault();
      update(event.clientX, event.clientY);
    });
    pad.addEventListener('pointerup', (event) => {
      if (pointerId !== event.pointerId) return;
      event.preventDefault();
      reset();
    });
    pad.addEventListener('pointercancel', reset);
  }
}
